import { memo } from "react";
import type { GoldenSerenityComponentProps } from "./types";
import { GS } from "./utils";
import { AmbientGlow, GoldHairline, OrganicDivider } from "./decorations";
import { normalizeMultilineText } from "@/utils/eventUtils";

/**
 * Godparents and Parents sections. Both bind to the event's free-text family
 * fields (event.family.godparents / event.family.parents) and render nothing
 * when the corresponding field is empty.
 */

type FamilyCardSectionProps = GoldenSerenityComponentProps & {
  id: string;
  title: string;
  text: string;
  gradient: string;
};

const FamilyCardSection = memo(({ config, id, title, text, gradient }: FamilyCardSectionProps) => (
  <section id={id} className="relative py-16 sm:py-24 overflow-hidden" style={{ background: gradient }}>
    <AmbientGlow />

    <div className="max-w-3xl mx-auto px-4 sm:px-6 relative z-10">
      <div className="text-center mb-10 sm:mb-14">
        <div className="mb-8">
          <OrganicDivider width={80} />
        </div>
        <h2
          className="text-4xl sm:text-5xl font-medium"
          style={{ color: config.palette.text, fontFamily: config.typography.heading }}
        >
          {title}
        </h2>
      </div>

      <div
        className="relative p-6 sm:p-10 rounded-xl text-center border"
        style={{
          background: `linear-gradient(to bottom right, ${GS.creamLight}, ${GS.creamDeep})`,
          borderColor: "rgba(232,220,200,0.4)",
          boxShadow: "0 6px 28px rgba(139,115,85,0.12)",
        }}
      >
        <GoldHairline className="mb-5" />
        <p
          className="text-xl sm:text-2xl font-medium whitespace-pre-line leading-relaxed"
          style={{ color: config.palette.text, fontFamily: config.typography.heading }}
        >
          {text}
        </p>
        <div className="mt-5">
          <OrganicDivider />
        </div>
      </div>
    </div>
  </section>
));
FamilyCardSection.displayName = "FamilyCardSection";

export const GoldenSerenityGodparents = memo((props: GoldenSerenityComponentProps) => {
  const { event, t } = props;
  const godparents = event.family?.godparents;
  if (!godparents) return null;

  return (
    <FamilyCardSection
      {...props}
      id="godparents"
      title={t("events.form.godparentsLabel")}
      text={normalizeMultilineText(godparents)}
      gradient={`linear-gradient(to bottom, ${GS.creamDeep}, ${GS.creamLight})`}
    />
  );
});
GoldenSerenityGodparents.displayName = "GoldenSerenityGodparents";

export const GoldenSerenityParents = memo((props: GoldenSerenityComponentProps) => {
  const { event, t } = props;
  const parents = event.family?.parents;
  if (!parents) return null;

  const includesWedding = event.includesWedding ?? true;
  const includesBaptism = event.includesBaptism ?? false;
  const isBaptismOnly = includesBaptism && !includesWedding;
  const title =
    isBaptismOnly && event.starBaptism
      ? t("events.form.parentsLabelBaptism", { baby: event.starBaptism })
      : t("events.form.parentsLabel");

  return (
    <FamilyCardSection
      {...props}
      id="parents"
      title={title}
      text={normalizeMultilineText(parents)}
      gradient={`linear-gradient(to bottom, ${GS.creamLight}, ${GS.creamDeep})`}
    />
  );
});
GoldenSerenityParents.displayName = "GoldenSerenityParents";
